/**
 * D8 Ashtamsha Division Attributes Data
 * Based on /Users/vn/src/hora/docs/new/ashta_amsha_table.md
 */

export interface AshtamshaDivisionAttributes {
  part: number;
  direction: string;
  ruler: string;
  yoga: string;
  enemy: string;
  manifestations: string;
}

/**
 * D8 Ashtamsha division attributes (8 parts of day)
 * Each part covers 45° (360° / 8 = 45°)
 * Based on /docs/new/ashta_amsha_table.md
 */
export const ASHTAMSHA_DIVISION_ATTRIBUTES: AshtamshaDivisionAttributes[] = [
  {
    part: 1,
    direction: "East",
    ruler: "Indra",
    yoga: "Yama",
    enemy: "Abhimana (pride)",
    manifestations: "Power, control, governance, connection with outer world, self-expression"
  },
  {
    part: 2,
    direction: "Northeast",
    ruler: "Ishana",
    yoga: "Niyama",
    enemy: "Samshaya (doubt, disbelief)",
    manifestations: "Spirituality, search for truth, inner light, devotion and service"
  },
  {
    part: 3,
    direction: "North",
    ruler: "Kubera",
    yoga: "Asana",
    enemy: "Lobha (greed)",
    manifestations: "Wealth, treasures, success, preservation, social status"
  },
  {
    part: 4,
    direction: "Northwest",
    ruler: "Vayu",
    yoga: "Pranayama",
    enemy: "Matsarya (envy, jealousy)",
    manifestations: "Strength, travels, movement, breath, inspiration, changes"
  },
  {
    part: 5,
    direction: "West",
    ruler: "Varuna",
    yoga: "Pratyahara",
    enemy: "Kama (lust)",
    manifestations: "Knowledge, purity, detachment, justice, choice, rewards"
  },
  {
    part: 6,
    direction: "Southwest",
    ruler: "Nirriti",
    yoga: "Dharana",
    enemy: "Krodha (anger, despair)",
    manifestations: "Destruction, trials, lessons, concentration, removing obstacles"
  },
  {
    part: 7,
    direction: "South",
    ruler: "Yama",
    yoga: "Dhyana",
    enemy: "Bhaya (fear)",
    manifestations: "Law, Dharma, destiny, judgment, death, meditation"
  },
  {
    part: 8,
    direction: "Southeast",
    ruler: "Agni",
    yoga: "Samadhi",
    enemy: "Moha (delusion)",
    manifestations: "Ceremony, ritual, Yajna, family hearth, fire of knowledge, prosperity"
  }
];

/**
 * Parse sunrise longitude to decimal degrees
 * Supports both formats:
 * - Number (decimal): 112.158686
 * - String DMS: "112° 09' 31.27""
 * - String decimal: "112.158686"
 *
 * @param sunriseValue - Sunrise longitude in any supported format
 * @returns Longitude in decimal degrees
 */
export function parseSunriseLongitude(sunriseValue: string | number | undefined | null): number {
  if (sunriseValue === undefined || sunriseValue === null) return 0;

  // Already a number - return directly
  if (typeof sunriseValue === 'number') {
    return isNaN(sunriseValue) ? 0 : sunriseValue;
  }

  // String - check format
  if (typeof sunriseValue === 'string') {
    // Check if it's a decimal string (e.g., "112.158686")
    if (/^[\d.]+$/.test(sunriseValue.trim())) {
      const decimal = parseFloat(sunriseValue);
      return isNaN(decimal) ? 0 : decimal;
    }

    // Parse DMS format: "112° 09' 31.27""
    const dmsMatch = sunriseValue.match(/(\d+)°\s*(\d+)'\s*([\d.]+)"/);
    if (dmsMatch) {
      const degrees = parseInt(dmsMatch[1]);
      const minutes = parseInt(dmsMatch[2]);
      const seconds = parseFloat(dmsMatch[3]);

      return degrees + (minutes / 60) + (seconds / 3600);
    }

    // Fallback: try to parse as number
    const decimal = parseFloat(sunriseValue);
    return isNaN(decimal) ? 0 : decimal;
  }

  return 0;
}

/**
 * Calculate which D8 part (1-8) a longitude falls into relative to sunrise
 * Each part is 45° starting from sunrise longitude (not 0° Mesha)
 * @param longitude - Planet longitude in degrees (0-360)
 * @param sunriseLongitude - Sunrise longitude in degrees (0-360)
 * @returns Part number (1-8)
 */
export function getLongitudeD8Part(longitude: number, sunriseLongitude: number = 0): number {
  // Normalize both longitudes to 0-360 range
  const normalizedLongitude = ((longitude % 360) + 360) % 360;
  const normalizedSunrise = ((sunriseLongitude % 360) + 360) % 360;

  // Calculate longitude relative to sunrise (sunrise = 0° for D8)
  const adjustedLongitude = ((normalizedLongitude - normalizedSunrise + 360) % 360);

  // Each part is 45°
  const partIndex = Math.floor(adjustedLongitude / 45);

  // Return 1-based part number
  return partIndex + 1;
}

/**
 * Parse longitude from position string format "rasi|degrees|longitude|speed"
 * @param position - Position string from API
 * @returns Longitude in degrees
 */
export function parseLongitudeFromPosition(position: string): number {
  const parts = position.split('|');
  if (parts.length >= 3) {
    return parseFloat(parts[2]);
  }
  return 0;
}